/**
 * The blocks a proposal is made of, to drag onto the page or click to add.
 *
 * A cover, the price and the terms appear once each: the customer's page, the
 * PDF and acceptance all read "the" pricing section, so a second would be
 * ambiguous rather than useful. Decision 81.
 */
import { useDraggable } from '@dnd-kit/core'
import type { QuoteSectionKindKey } from '@fieldops/shared'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { BLOCK_ICON } from './CanvasBlock.js'

export const SINGLE_BLOCKS: ReadonlySet<QuoteSectionKindKey> = new Set(['COVER', 'PRICING', 'TERMS'])

export const BLOCK_KINDS: Array<{ kind: QuoteSectionKindKey; label: string; description: string }> = [
  { kind: 'TEXT', label: 'Text', description: 'A heading and words — what you found, the plan, why us.' },
  {
    kind: 'IMAGE',
    label: 'Photos & files',
    description: 'A row of photos and PDFs — a chart, a brochure, what you found.',
  },
  { kind: 'PRICING', label: 'Pricing', description: 'Line items or options to choose from, and extras to tick.' },
  { kind: 'COVER', label: 'Cover', description: 'A big headline and introduction at the top.' },
  { kind: 'TERMS', label: 'Terms', description: 'The fine print. Blank prints your default terms.' },
]

export const LIBRARY_PREFIX = 'library:'

export function BlockLibrary({
  present,
  onAdd,
  draggable = true,
}: {
  present: ReadonlySet<QuoteSectionKindKey>
  onAdd: (kind: QuoteSectionKindKey) => void
  /** Off in the phone's sheet, where a tap is the only gesture. */
  draggable?: boolean
}) {
  return (
    <ul className="flex flex-col gap-1.5" aria-label="Blocks">
      {BLOCK_KINDS.map((block) => {
        const used = SINGLE_BLOCKS.has(block.kind) && present.has(block.kind)
        return (
          <li key={block.kind}>
            <LibraryItem
              kind={block.kind}
              label={block.label}
              description={used ? 'Already on the page.' : block.description}
              disabled={used}
              draggable={draggable}
              onAdd={() => onAdd(block.kind)}
            />
          </li>
        )
      })}
    </ul>
  )
}

function LibraryItem({
  kind,
  label,
  description,
  disabled,
  draggable,
  onAdd,
}: {
  kind: QuoteSectionKindKey
  label: string
  description: string
  disabled: boolean
  draggable: boolean
  onAdd: () => void
}) {
  const drag = useDraggable({ id: `${LIBRARY_PREFIX}${kind}`, data: { kind }, disabled: disabled || !draggable })

  return (
    <button
      ref={drag.setNodeRef}
      type="button"
      disabled={disabled}
      aria-label={`Add ${label.toLowerCase()}`}
      onClick={onAdd}
      {...(draggable ? drag.listeners : {})}
      {...(draggable ? drag.attributes : {})}
      className={cn(
        'flex w-full items-start gap-3 rounded-xl bg-white p-2.5 text-left ring-1 ring-ink-200 transition-all',
        disabled
          ? 'cursor-default opacity-50'
          : 'hover:-translate-y-px hover:shadow-md hover:ring-ink-300 active:translate-y-0',
        draggable && !disabled && 'cursor-grab touch-none active:cursor-grabbing',
        drag.isDragging && 'opacity-40',
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-ink-50 text-ink-700 ring-1 ring-ink-200">
        <Icon name={BLOCK_ICON[kind]} className="size-4.5" />
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-ink-900">{label}</span>
        <span className="block text-[11px] leading-4 text-ink-500">{description}</span>
      </span>
    </button>
  )
}

/** What travels under the pointer while a block is dragged in from the library. */
export function LibraryGhost({ kind }: { kind: QuoteSectionKindKey }) {
  const block = BLOCK_KINDS.find((entry) => entry.kind === kind)
  return (
    <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-ink-900 shadow-xl ring-2 ring-[var(--p-brand)]">
      <Icon name={BLOCK_ICON[kind]} className="size-4" />
      {block?.label}
    </div>
  )
}
